import { useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { cn } from '../lib/utils';

/**
 * Subdomain entry field: a half-width input followed by the read-only base domain, so the
 * user can see the full address they are creating (e.g. "acmelaw" + ".example.com").
 *
 * The suffix is a plain <span> — it is display only and can never be edited or submitted.
 * Only the bare subdomain is sent to the API. When `baseDomain` is empty the suffix is
 * omitted entirely rather than rendering a dangling dot.
 *
 * `baseDomain` is passed in rather than read from config here so this stays a pure
 * presentational component: importing config.js pulls in `import.meta.env`, which Jest
 * cannot parse, and that would make this component untestable.
 *
 * FormField is not reused because it has no suffix/addon slot and hardcodes the inner
 * input's className.
 *
 * @param {{
 *   id: string,
 *   label: string,
 *   baseDomain?: string,
 *   previewValue?: string,
 *   error?: string,
 *   action?: React.ReactNode,
 *   children?: React.ReactNode,
 *   className?: string,
 *   inputClassName?: string,
 *   labelClassName?: string,
 *   errorClassName?: string,
 *   suffixClassName?: string,
 *   dataTestId?: string,
 * }} props - Remaining props are spread onto the input.
 */
export function SubdomainField({
  id,
  label,
  baseDomain = '',
  previewValue,
  error,
  action,
  children,
  className = '',
  inputClassName = '',
  labelClassName = '',
  errorClassName = '',
  suffixClassName = '',
  dataTestId,
  ...inputProps
}) {
  const domainId = `${id}-domain`;
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  // `previewValue` is needed because react-hook-form's register() supplies no `value` — the
  // signup form passes its watched value explicitly. Controlled callers (profile) already
  // pass `value`, so they need nothing extra.
  const current = String(previewValue ?? inputProps.value ?? '').trim();
  // Includes the scheme so the copied value is a complete, pasteable URL rather than a bare host.
  const fullAddress = `https://${current}.${baseDomain}`;
  // Hidden while invalid or empty so we never show a dangling ".example.com".
  const showPreview = !!baseDomain && !!current && !error;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullAddress);
      setCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (insecure context / denied) — the address stays readable on screen.
    }
  };

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label
        htmlFor={id}
        className={labelClassName}
        data-testid={dataTestId ? `${dataTestId}-label` : undefined}
      >
        {label}
      </Label>

      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="text"
          aria-invalid={!!error}
          // Screen readers announce the suffix with the field, so the full address is conveyed.
          aria-describedby={baseDomain ? domainId : undefined}
          // flex-1 on both the input and the domain box splits the row into equal halves.
          // min-w-0 lets the domain box truncate instead of forcing the row to overflow.
          className={cn('h-12 min-w-0 flex-1', inputClassName)}
          data-testid={dataTestId}
          {...inputProps}
        />

        {baseDomain && (
          // Boxed to match the input, but muted and borderless-on-focus so it reads as
          // read-only. Still a <span>, so it can never be edited or submitted.
          <span
            id={domainId}
            className={cn(
              'flex h-12 min-w-0 flex-1 items-center rounded-md border border-input bg-muted px-3',
              suffixClassName
            )}
            title={`.${baseDomain}`}
            data-testid={dataTestId ? `${dataTestId}-domain` : undefined}
          >
            {/* Matches the input's text size now that the halves are equal; truncate + the title
                attribute above are the fallback if a longer domain is ever configured. */}
            <span className="truncate text-sm text-muted-foreground">.{baseDomain}</span>
          </span>
        )}

        {action}
      </div>

      {error && (
        <p
          className={cn('text-xs text-danger-fg', errorClassName)}
          data-testid={dataTestId ? `${dataTestId}-error` : undefined}
        >
          {error}
        </p>
      )}

      {/* Status text (checking / available) sits above the address line. */}
      {children}

      {showPreview && (
        <div
          className="flex items-center gap-1.5"
          data-testid={dataTestId ? `${dataTestId}-preview` : undefined}
        >
          <span className="shrink-0 text-xs text-muted-foreground">Your address:</span>
          <span
            className="min-w-0 truncate font-mono text-xs text-foreground"
            title={fullAddress}
            data-testid={dataTestId ? `${dataTestId}-address` : undefined}
          >
            {fullAddress}
          </span>
          <button
            type="button"
            onClick={handleCopy}
            aria-label={copied ? 'Address copied' : 'Copy address'}
            className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
            data-testid={dataTestId ? `${dataTestId}-copy` : undefined}
          >
            {copied
              ? <Check className="h-3.5 w-3.5 text-success-fg" aria-hidden="true" />
              : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
          </button>
          {/* Announces the copy to screen readers, which get no visual icon change. */}
          <output className="sr-only" aria-live="polite">
            {copied ? 'Address copied to clipboard' : ''}
          </output>
        </div>
      )}
    </div>
  );
}
